import { db } from "@/firebase";
import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { RootState } from "@/store/store";
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from "firebase/firestore";
import { ApiError } from "@/types/api";
import { Branch } from "@/types/branch";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { pick } from "lodash";
import { updateOrganization, updateOrganizationLocal } from "../slice";

export const branchApi = createApi({
  reducerPath: "branchApi",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Branch"],
  endpoints: (builder) => ({
    fetchBranch: builder.query<Branch[], string | SkipToken>({
      queryFn: async (organizationId: string) => {
        if (!organizationId) {
          return {
            error: {
              error: {
                status: 500,
                statusText: "Consulta Inválida",
              },
            },
          };
        }
        try {
          const collectionRef = collection(db, `organizations/${String(organizationId)}/branches`);

          const branches = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as Branch[]);
          });

          if (!branches.length) {
            return { data: [] };
          }

          return { data: branches };
        } catch (error) {
          return {
            error: {
              error: {
                status: 500,
                statusText: "Error al obtener las sucursales",
              }
            }
          }
        }
      },
      providesTags: ["Branch"],
      onQueryStarted: async (organizationId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (organizationId && typeof organizationId === "string") {
            dispatch(updateOrganizationLocal({ id: organizationId, branches: data }));
          }
        } catch (error) {
          console.error("Error fetching branches:", error);
        }
      },
    }),
    addBranch: builder.mutation<Branch | ApiError, { draft: Branch; organizationId: string }>({
      queryFn: async ({ draft, organizationId }, { dispatch, getState }) => {
        const TASK = "Crear Sucursal";
        if (!organizationId || !draft) {
          return {
            error: {
              error: {
                status: 500,
                statusText: "Consulta inválida",
              },
            },
          };
        }

        const store = getState() as RootState;

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/branches`);

          const ALLOWED_FIELDS = ["title", "location", "whatsapp", "schedule", "images", "isMainBranch", "services"];

          const payload: Partial<Branch> = {
            ...pick(draft, ALLOWED_FIELDS),
            createdBy: store.auth.user?.uid || null,
            createdAt: Date.now(),
          };

          const docRef = await addDoc(collectionRef, payload);

          // actualiza la organización con la nueva sucursal en redux
          const currentOrg = store.organizations[organizationId];
          dispatch(
            updateOrganization({
              id: organizationId,
              branches: [...(currentOrg?.branches ?? []), { ...payload, id: docRef.id } as Branch],
            })
          );

          return {
            data: {
              ...payload,
              id: docRef.id,
            } as Branch,
          };
        } catch (error) {
          return {
            error: {
              error: {
                status: 500,
                statusText: "Error al crear la sucursal",
              },
            },
          };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Branch"],
    }),

    updateBranch: builder.mutation<
      Branch,
      {
        organizationId: string | SkipToken;
        branchId: string;
        branch: Branch;
      }
    >({
      queryFn: async ({ organizationId, branchId, branch }, { getState, dispatch }) => {
        const TASK = "Actualizando contenido";

        const ALLOWED_FIELDS = ["title", "whatsapp", "location", "schedule", "images", "isMainBranch", "services"];

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as any;

          if (!store.auth?.user?.uid || !organizationId || !branchId) {
            throw new Error("Invalid payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/branches/${String(branchId)}`);

          const payload = {
            ...pick(branch, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user?.uid || null,
            lastUpdatedAt: Date.now(),
          };

          await updateDoc(docRef, payload);

          // Actualizar la lista de sucursales en el store
          const currentBranches = store.organizations[organizationId as string]?.branches ?? [];
          const nextBranches = currentBranches.map((b: Branch) => (b.id === branchId ? { ...b, ...payload } : b));
          dispatch(updateOrganization({ id: organizationId as string, branches: nextBranches }));

          return { data: { ...branch, ...payload, id: branchId } as Branch };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Branch"],
    }),
    deleteBranch: builder.mutation<
      { success: boolean },
      {
        organizationId: string | SkipToken;
        branchId: string;
      }
    >({
      queryFn: async ({ organizationId, branchId }, { getState, dispatch }) => {
        if (!branchId || !organizationId) {
          return {
            error: {
              error: {
                status: 500,
                statusText: "Consulta Inválida",
              },
            },
          };
        }

        const docRef = doc(db, `organizations/${String(organizationId)}/branches/${String(branchId)}`);
        try {
          await deleteDoc(docRef);

          // Actualizar la lista de sucursales en el store
          const store = getState() as any;
          const currentBranches = store.organizations[organizationId as string]?.branches ?? [];
          const nextBranches = currentBranches.filter((b: Branch) => b.id !== branchId);
          dispatch(updateOrganization({ id: organizationId as string, branches: nextBranches }));

          return { data: { success: true } };
        } catch (error) {
          console.error(error);
          return {
            error: {
              error: {
                status: 500,
                statusText: "Error al eliminar la sucursal",
              },
            },
          };
        }
      },
      invalidatesTags: ["Branch"],
    }),
  }),
});

export const { useFetchBranchQuery, useLazyFetchBranchQuery, useAddBranchMutation, useUpdateBranchMutation, useDeleteBranchMutation } = branchApi;
